import { readUsernameFromPayload, stripUsernameAt } from '../../utils/usernameFormat';
import { PUSH_ROUTE_PATHS } from './pushConstants';
import { isIncomingCallRequestPush } from './pushCallNotificationPolicy';
import { normalizePushText, readPushTypeKeys } from './pushPayloadCore';

const NEW_CONTENT_BODY_USERNAME =
  /^@?([A-Za-z0-9_.-]+)\s+(?:has\s+)?posted new content$/i;

const readUsernameFromNewContentBody = (bodyText = '') => {
  const text = normalizePushText(bodyText);
  if (!text) return '';

  const match = text.match(NEW_CONTENT_BODY_USERNAME);
  return match ? stripUsernameAt(match[1]) : '';
};

export const buildNewMessageChatLink = (data = {}) => {
  const { type, eventType } = readPushTypeKeys(data);
  const isMessageEvent = type === 'new_message' || eventType === 'message_received';

  if (!isMessageEvent) {
    return '';
  }

  const senderUserId = normalizePushText(
    data?.senderUserId ||
      data?.senderId ||
      data?.fromUserId ||
      data?.from_user_id ||
      data?.userId
  );

  if (!senderUserId) {
    return '';
  }

  return `/my/messages/chat/${encodeURIComponent(senderUserId)}`;
};

export const buildIncomingCallChatLink = (data = {}) => {
  if (!isIncomingCallRequestPush(data)) {
    return '';
  }

  const callerUserId = normalizePushText(data?.callerUserId);
  if (!callerUserId) {
    return '';
  }

  return `/my/messages/chat/${encodeURIComponent(callerUserId)}`;
};

export const buildCreatorSalesTipActivityLink = (data = {}, activityLink = '/activity') => {
  const { type, eventType } = readPushTypeKeys(data);
  const isCreatorSalesTip = type === 'creator_sales_tips' || eventType === 'tip_received';

  return isCreatorSalesTip ? activityLink : '';
};

export const buildPriorityMessagesBannerLink = (data = {}) => {
  const { type, eventType } = readPushTypeKeys(data);
  const isPriorityMessagesBanner =
    type === 'creator_priority_messages' ||
    eventType === 'unread_priority_messages_banner';

  if (!isPriorityMessagesBanner) {
    return '';
  }

  return (
    normalizePushText(data?.openUrl) ||
    normalizePushText(data?.deepLink) ||
    normalizePushText(data?.link) ||
    PUSH_ROUTE_PATHS.PRIORITY_MESSAGES
  );
};

export const buildTrendingProfileLink = (data = {}) => {
  const { type, eventType } = readPushTypeKeys(data);
  const isTrendingProfile =
    type === 'trending' ||
    type === 'creator_trending' ||
    eventType === 'trending_profile' ||
    eventType === 'trending_profile_started';

  if (!isTrendingProfile) {
    return '';
  }

  const openUrl = normalizePushText(data?.openUrl);
  if (openUrl) {
    return openUrl;
  }

  const deepLink = normalizePushText(data?.deepLink);
  if (deepLink) {
    return deepLink;
  }

  const creatorId = normalizePushText(data?.creator_id || data?.creatorId);
  if (creatorId) {
    return `/creator/${encodeURIComponent(creatorId)}`;
  }

  const username =
    stripUsernameAt(readUsernameFromPayload(data)) ||
    stripUsernameAt(data?.creator_username || data?.creatorUsername);

  if (username) {
    return `/${encodeURIComponent(username)}`;
  }

  return '';
};

/** Subscriber "new content" pushes open the creator profile when tapped. */
export const buildNewContentCreatorProfileLink = (data = {}, bodyText = '') => {
  const { type } = readPushTypeKeys(data);
  if (type !== 'new_content') {
    return '';
  }

  const username =
    stripUsernameAt(readUsernameFromPayload(data)) ||
    readUsernameFromNewContentBody(bodyText) ||
    readUsernameFromNewContentBody(data?.message) ||
    readUsernameFromNewContentBody(data?.body);

  if (!username) {
    return '';
  }

  return `/${encodeURIComponent(username)}`;
};

/**
 * Resolves a navigation target from an already-flattened push data object.
 * @param {object} data
 * @param {{ bodyText?: string, defaultLink?: string }} [options]
 */
export const resolvePushLinkFromData = (data = {}, { bodyText = '', defaultLink = '' } = {}) =>
  buildCreatorSalesTipActivityLink(data, defaultLink) ||
  buildPriorityMessagesBannerLink(data) ||
  buildTrendingProfileLink(data) ||
  buildNewContentCreatorProfileLink(data, bodyText) ||
  buildNewMessageChatLink(data) ||
  normalizePushText(data?.openUrl) ||
  normalizePushText(data?.deepLink) ||
  normalizePushText(data?.link) ||
  normalizePushText(data?.url) ||
  normalizePushText(data?.click_action) ||
  normalizePushText(data?.path) ||
  buildIncomingCallChatLink(data) ||
  defaultLink;
